"use client";

import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { GEO_PREF_UPDATED_EVENT } from "@/lib/geo/geo-pref";
import {
	fetchPricingResolveItems,
	pricingItemsById,
	readPricingReferenceCurrency,
} from "@/lib/pricing/pricing-resolve-client";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";

/**
 * Resolves reference/display labels for one or more money amounts (same policy as cart / PLP).
 * Returns an empty Map while loading or when `items` is empty; callers fall back to `Intl` formatting.
 * Invalidates the whole map when amounts or the request's locale/channel/geo context change.
 */
function subscribePreference(listener: () => void) {
	window.addEventListener(GEO_PREF_UPDATED_EVENT, listener);
	return () => window.removeEventListener(GEO_PREF_UPDATED_EVENT, listener);
}
const serverReferenceCurrency = () => null;

export function usePricingResolvePresentations(
	locale: string,
	channel: string,
	items: Array<{ id: string; amount: number; currency: string }>,
	/**
	 * Display currency pinned by the surrounding page (the US ads landing page quotes the
	 * channel settlement currency). When set it replaces the visitor's `geo_pref_v2`
	 * preference in BOTH the request and the cache key, so a pinned surface can never serve
	 * a label the page around it contradicts. `null`/omitted keeps the cookie behaviour.
	 */
	referenceCurrencyOverride?: string | null,
): Map<string, ResolvedMoneyPresentation> {
	const itemsKey = JSON.stringify(items.map(({ id, amount, currency }) => [id, amount, currency]));
	const preference = useSyncExternalStore(
		subscribePreference,
		readPricingReferenceCurrency,
		serverReferenceCurrency,
	);
	const referenceCurrency = referenceCurrencyOverride ?? preference;
	// country:null is the existing request policy; IP-country remains resolved by the server.
	const key = JSON.stringify([locale, channel, null, referenceCurrency, itemsKey]);

	// Identity-stabilize `items` on content so caller arrays with fresh identity cannot retrigger the fetch.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const stableItems = useMemo(() => items, [itemsKey]);

	const [resolved, setResolved] = useState<{
		key: string;
		map: Map<string, ResolvedMoneyPresentation>;
	} | null>(null);
	useEffect(() => {
		if (stableItems.length === 0) {
			return;
		}
		const requestKey = key;
		const controller = new AbortController();
		void fetchPricingResolveItems({
			locale,
			channel,
			items: stableItems,
			referenceCurrency,
			signal: controller.signal,
		}).then((res) => {
			if (controller.signal.aborted || !res) return;
			setResolved({ key: requestKey, map: pricingItemsById(res) });
		});
		return () => {
			controller.abort();
		};
	}, [locale, channel, key, stableItems, referenceCurrency]);

	if (items.length === 0) {
		return new Map();
	}
	if (!resolved || resolved.key !== key) {
		return new Map();
	}
	return resolved.map;
}
